import {
  Check,
  Clock3,
  Clipboard,
  Crown,
  Link as LinkIcon,
  Moon,
  Volume2,
  VolumeX,
  WifiOff,
  Armchair,
  AudioLines,
  Pencil,
} from "lucide-react";
import { useState, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from "react";
import type { PublicPlayer, VoiceParticipant } from "@ma-soi/game-core";
import { SeatAvatar } from "./SeatAvatar";

type ButtonVariant = "primary" | "secondary" | "danger" | "gold";

export function AnimatedLandingBackground({
  variant = "hero",
}: {
  variant?: "hero" | "lobby" | "compact";
}) {
  return (
    <div className={`cinematic-bg cinematic-bg-${variant}`} aria-hidden="true">
      <div className="cinematic-bg-scene" />
      <div className="cinematic-bg-overlay" />
    </div>
  );
}

export function AnimatedTitle({
  kicker,
  title = "Ma Sói: Đêm Trăng Máu",
  subtitle,
}: {
  kicker?: string;
  title?: string;
  subtitle?: string;
}) {
  return (
    <div className="animated-title-block">
      {kicker && <p className="cinematic-eyebrow">{kicker}</p>}
      <h1 className="animated-title">{title}</h1>
      {subtitle && <p className="animated-subtitle">{subtitle}</p>}
    </div>
  );
}

export function GameButton({
  variant = "primary",
  icon,
  trailingIcon,
  fullWidth = false,
  dramatic = false,
  small = false,
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  icon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
  dramatic?: boolean;
  small?: boolean;
}) {
  return (
    <button
      {...props}
      className={[
        "game-button",
        `game-button-${variant}`,
        fullWidth ? "game-button-full" : "",
        dramatic ? "game-button-dramatic" : "",
        small ? "game-button-small" : "",
        className,
      ].filter(Boolean).join(" ")}
    >
      {icon}
      <span>{children}</span>
      {trailingIcon}
    </button>
  );
}

export function SurfacePanel({
  children,
  className = "",
  variant = "soft",
}: {
  children: ReactNode;
  className?: string;
  variant?: "soft" | "ritual" | "tavern" | "council";
}) {
  return <section className={`surface-panel surface-panel-${variant} ${className}`}>{children}</section>;
}

export function SettingsToggle({
  checked,
  onChange,
  title,
  description,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label className={`settings-toggle settings-rune-toggle ${checked ? "checked" : ""} ${disabled ? "disabled" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="settings-rune-toggle-mark" aria-hidden="true"><Check /></span>
      <span className="settings-rune-toggle-copy">
        <strong>{title}</strong>
        {description && <small>{description}</small>}
      </span>
    </label>
  );
}

export function RoomSeal({
  code,
  copied,
  onCopy,
}: {
  code: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <button className={`room-seal ${copied ? "copied" : ""}`} onClick={onCopy} type="button" aria-label={`Sao chép mã phòng ${code}`}>
      <span className="room-seal-label">Mã phòng</span>
      <strong>{code}</strong>
      {copied ? <Check /> : <Clipboard />}
    </button>
  );
}

export function InviteScroll({
  href,
  copied,
  onCopy,
}: {
  href: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className={`invite-scroll ${copied ? "copied" : ""}`}>
      <LinkIcon />
      <span className="invite-scroll-text">{href}</span>
      <button type="button" onClick={onCopy}>{copied ? "Đã sao chép" : "Sao chép lời mời"}</button>
    </div>
  );
}

export function LobbyCouncilBoard({
  title,
  count,
  max,
  children,
}: {
  title: string;
  count: number;
  max: number;
  children: ReactNode;
}) {
  return (
    <section className="lobby-council-board">
      <div className="lobby-council-board-header">
        <div>
          <p className="cinematic-eyebrow">Dân làng đang tụ họp</p>
          <h2>{title}</h2>
        </div>
        <span>{count}/{max}</span>
      </div>
      {children}
    </section>
  );
}

export function PlayerLobbyCard({
  player,
  voice,
  index,
  isSelf = false,
  onChangeAvatar,
  avatarDisabled = false,
}: {
  player: PublicPlayer;
  voice?: VoiceParticipant;
  index: number;
  isSelf?: boolean;
  onChangeAvatar?: () => void;
  avatarDisabled?: boolean;
}) {
  const offline = player.connected === false;
  const speaking = !offline && voice?.connected && voice.isSpeaking;
  return (
    <article
      className={[
        "player-lobby-card",
        player.ready ? "ready" : "not-ready",
        player.isHost ? "host" : "",
        offline ? "offline" : "",
        isSelf ? "is-self" : "",
        speaking ? `voice-speaking voice-${voice.channel}` : "",
      ].filter(Boolean).join(" ")}
      style={{ "--card-delay": `${Math.min(index, 3) * 25}ms` } as CSSProperties}
    >
      <div className="seat-card-top"><span>Ghế {String(player.seat).padStart(2, "0")}</span>{player.isHost ? <span className="seat-host"><Crown aria-hidden="true" />Chủ phòng</span> : isSelf && <span>Bạn</span>}</div>
      {isSelf && onChangeAvatar ? (
        <button type="button" className="seat-avatar-change" onClick={onChangeAvatar} disabled={avatarDisabled} aria-label="Đổi avatar" aria-haspopup="dialog">
          <SeatAvatar seat={player.seat} avatarId={player.avatarId} name={player.name} muted={offline} />
          <span className="seat-avatar-change-label"><Pencil aria-hidden="true" />Đổi avatar</span>
        </button>
      ) : <SeatAvatar seat={player.seat} avatarId={player.avatarId} name={player.name} muted={offline} />}
      <strong title={player.name}>{player.name}{isSelf && <span className="sr-only"> (Bạn)</span>}</strong>
      <span className={`seat-status ${offline ? "seat-offline" : player.ready ? "seat-ready" : ""}`}>
        {offline ? <WifiOff /> : player.ready ? <Check /> : <Clock3 />}
        {offline ? "Mất kết nối" : player.ready ? "Sẵn sàng" : "Chưa sẵn sàng"}
      </span>
      <span className="seat-voice-status">{speaking ? <><AudioLines aria-hidden="true" />Đang nói</> : isSelf ? "Ghế của bạn" : ""}</span>
    </article>
  );
}

export function EmptySeatCard({ index }: { index: number }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="player-lobby-card empty-seat" aria-label={`Ghế trống ${index}`}>
      <div className="seat-card-top"><span>Ghế {String(index).padStart(2, "0")}</span></div>
      <div className="empty-seat-art" aria-hidden="true">
        <span className="empty-seat-glow" />
        {failed ? <Armchair /> : <img src="/assets/ui/gothic-v2/empty-seat.webp" alt="" width="512" height="512" loading="lazy" draggable={false} onError={() => setFailed(true)} />}
        <i /><i />
      </div>
      <strong>Ghế trống</strong>
      <small>Đợi người tham gia</small>
    </div>
  );
}

export function SoundToggleButton({
  enabled,
  onToggle,
  className = "",
}: {
  enabled: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      className={`sound-toggle-button ${enabled ? "enabled" : ""} ${className}`}
      onClick={onToggle}
      aria-label={enabled ? "Tắt âm thanh" : "Bật âm thanh"}
      title={enabled ? "Tắt âm thanh" : "Bật âm thanh"}
      type="button"
    >
      {enabled ? <Volume2 /> : <VolumeX />}
      <span>Âm thanh: {enabled ? "Bật" : "Tắt"}</span>
    </button>
  );
}

export function PhaseScene({ dawn = false }: { dawn?: boolean }) {
  return (
    <div className={`phase-scene ${dawn ? "phase-scene-dawn" : ""}`} aria-hidden="true">
      <Moon />
    </div>
  );
}
